import { t } from "i18next";
import { Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { emptyUser, User } from "../../interfaces/user";
import { getUserSingle, updateUserProfile } from "../../services/user";
import { LoadingSpinner } from "../general/loading-spinner";
import Uploader from "../general/uploader";
import { Media } from "../../interfaces/media";
import { getAthleteSingle } from "../../services/athlete";
import { getStaffSingle } from "../../services/staff";
import { countriesList } from "../../data/countries";

interface ProfileEditProps {
    id: number;
    role: any[];
    referenceId: number;
    onSave: CallableFunction;
    onClose: CallableFunction;
}

interface LocationData {
    birth_city: string;
    birth_region: string;
    birth_country: string;
    address: string;
    city: string;
    region: string;
    country: string;
}

const emptyLocationData: LocationData = {
    birth_city: '',
    birth_region: '',
    birth_country: '',
    address: '',
    city: '',
    region: '',
    country: ''
};

export function ProfileEdit(props: ProfileEditProps) {
    const [user, setUser] = useState<User>(emptyUser);
    const [media, setMedia] = useState<Media[]>([]);
    const [locationData, setLocationData] = useState<LocationData>(emptyLocationData);

    const isAthlete = props.role.filter((r: any) => r.id == 'padeltech_athlete').length > 0;
    const isCoach = props.role.filter((r: any) => r.id == 'padeltech_coach').length > 0;
    const hasLocationFields = (isAthlete || isCoach) && props.referenceId > 0;

    useEffect(() => {
        getUserSingle(props.id)
            .then(response => {
                setUser(response.data);
                if (response.data.avatar && response.data.avatar.length > 0) {
                    const newMedia = response.data.avatar.map((m: Media) => { return m; });
                    setMedia(newMedia);
                }
            })
            .catch(error => {
                console.log(error);
            });

        if (hasLocationFields) {
            const fetchLocationData = isAthlete
                ? getAthleteSingle(props.referenceId)
                : getStaffSingle(props.referenceId);

            fetchLocationData
                .then(response => {
                    const data = response.data;
                    setLocationData({
                        birth_city: data.birth_city || '',
                        birth_region: data.birth_region || '',
                        birth_country: data.birth_country || '',
                        address: data.address || '',
                        city: data.city || '',
                        region: data.region || '',
                        country: data.country || ''
                    });
                })
                .catch(error => {
                    console.log(error);
                });
        }
    }, []);

    const handleSave = (event: any) => {
        event.preventDefault();

        const body: any = {
            id: event.target.id.value,
            first_name: event.target.first_name.value,
            last_name: event.target.last_name.value,
            phone: event.target.phone.value,
            document_id: event.target.document_id.value,
            birthdate: event.target.birthdate.value,
            description: event.target.description.value,
            avatar: media
        }

        if (hasLocationFields) {
            body.birth_city = event.target.birth_city.value;
            body.birth_region = event.target.birth_region.value;
            body.birth_country = event.target.birth_country.value;
            body.address = event.target.address.value;
            body.city = event.target.city.value;
            body.region = event.target.region.value;
            body.country = event.target.country.value;
        }

        updateUserProfile(body)
            .then(response => {
                if (response.data.status == 1) {
                    props.onSave(response.data.data);
                } else {
                    alert(response.data.message);
                }
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleClose = () => {
        props.onClose();
    }

    const handleMediaEventUpload = (media: Media[]) => {
        if (media.length > 1) {
            alert(t("single_image_warning"));
        } else {
            const newMedia = media.map((m: Media) => { return m; });
            setMedia(newMedia);
        }
    }

    return (
        <Modal className="admin-user-new-container" show={ true }
            onHide={ handleClose } size="xl">
            <Modal.Header closeButton>
                <Modal.Title>
                    { t("edit_profile") }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { user.id == 0 &&
                    <LoadingSpinner />
                }
                { user.id != 0 &&
                    <Form onSubmit={ handleSave }>
                        <div className="fields">
                            <Container>
                                <Row>
                                    <Form.Control type="hidden" name="id" required defaultValue={ user.id }/>
                                    <Col xs={12} md={8}>
                                        <Row>
                                            <Col xs={12} md={6}>
                                                <Form.Group className="mb-3" controlId="first_name">
                                                    <Form.Label aria-required="true">{ t("first_name") }</Form.Label>
                                                    <Form.Control type="text" name="first_name" required defaultValue={ user.first_name }/>
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Group className="mb-3" controlId="last_name">
                                                    <Form.Label aria-required="true">{ t("last_name") }</Form.Label>
                                                    <Form.Control type="text" name="last_name" required defaultValue={ user.last_name } />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Group className="mb-3" controlId="email">
                                                    <Form.Label>{ t("email") }</Form.Label>
                                                    <Form.Control type="text" name="email" defaultValue={ user.email } disabled />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Group className="mb-3" controlId="phone">
                                                    <Form.Label>{ t("phone") }</Form.Label>
                                                    <Form.Control type="text" name="phone" defaultValue={ user.phone } />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Group className="mb-3" controlId="document_id">
                                                    <Form.Label>{ t("document_id") }</Form.Label>
                                                    <Form.Control type="text" name="document_id" defaultValue={ user.document_id } />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Group className="mb-3" controlId="birthdate">
                                                    <Form.Label>{ t("birthdate") }</Form.Label>
                                                    <Form.Control type="date" name="birthdate" required defaultValue={ user.birthdate } />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12}>
                                                <Form.Group className="mb-3" controlId="description">
                                                    <Form.Label>{ t("description") }</Form.Label>
                                                    <Form.Control name="description" as="textarea" rows={5} defaultValue={ user.description } />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12}>
                                                <Form.Group className="mb-3 media-field" controlId="media">
                                                    <Uploader title={ t("profile_avatar_upload_message") }
                                                        media={ media } save={ handleMediaEventUpload } />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Col>
                                    { hasLocationFields &&
                                        <Col xs={12} md={4} style={ { borderLeft: '1px solid #000;'} }>
                                            <div className="left mt-3">
                                                <fieldset className="mb-4">
                                                    <legend>{ t("birth_place") }</legend>
                                                    <div>
                                                        <Form.Group className="mb-2" controlId="birth_city">
                                                            <Form.Label>{ t("birth_city") }</Form.Label>
                                                            <Form.Control type="text" name="birth_city" defaultValue={ locationData.birth_city } />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2" controlId="birth_region">
                                                            <Form.Label>{ t("birth_region") }</Form.Label>
                                                            <Form.Control type="text" name="birth_region" defaultValue={ locationData.birth_region } />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2" controlId="birth_country">
                                                            <Form.Label>{ t("birth_country") }</Form.Label>
                                                            <Form.Select aria-label={ t("birth_country") } name="birth_country" defaultValue={ locationData.birth_country }>
                                                                <option value="">{ t("select_country") }</option>
                                                                { countriesList.map((c: any) => {
                                                                    return <option key={ 'birth-country-' + c.code }
                                                                        value={ c.code }>{ c.name }</option>
                                                                })}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </div>
                                                </fieldset>
                                                <fieldset className="">
                                                    <legend>{ t("residence_place") }</legend>
                                                    <div>
                                                        <Form.Group className="mb-2" controlId="address">
                                                            <Form.Label>{ t("address") }</Form.Label>
                                                            <Form.Control type="text" name="address" defaultValue={ locationData.address } />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2" controlId="city">
                                                            <Form.Label>{ t("city") }</Form.Label>
                                                            <Form.Control type="text" name="city" defaultValue={ locationData.city } />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2" controlId="region">
                                                            <Form.Label>{ t("region") }</Form.Label>
                                                            <Form.Control type="text" name="region" defaultValue={ locationData.region } />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2" controlId="country">
                                                            <Form.Label>{ t("country") }</Form.Label>
                                                            <Form.Select aria-label={ t("country") } name="country" defaultValue={ locationData.country }>
                                                                <option value="">{ t("select_country") }</option>
                                                                { countriesList.map((c: any) => {
                                                                    return <option key={ 'country-' + c.code }
                                                                        value={ c.code }>{ c.name }</option>
                                                                })}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </div>
                                                </fieldset>
                                            </div>
                                        </Col>
                                    }
                                </Row>
                            </Container>
                        </div>
                        <div className="actions">
                            <Button type="submit" variant="primary">
                                { t("Save") }
                            </Button>
                            <Button variant="secondary" onClick={ handleClose }>
                                { t("Close") }
                            </Button>
                        </div>
                    </Form>
                }
            </Modal.Body>
        </Modal>
    );
}
